import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { TEXT_DIRECTION } from 'helpers/isRtlLanguage';
import PropTypes from 'prop-types';
import { CDN_TYPE_CMS, imageUrlCDN } from 'utils';

import { Slider } from '@atlas/components';
import { SiteConfigManager, useApplicationState } from '@atlas/core';
import { casinoRoutes, COMPONENT_TYPES } from '@atlas/routing';
import { CasinoPrimaryTile, Label } from '@atlas/ui';

import { BASE_PATH } from 'common/constants';
import useSignInRedirects from 'components/Auth/useSignInRedirects';
import { useIsRtl } from 'hooks/useIsRtl';

import { LOBBY_PRIMARY_ACTION_TYPES, LOBBY_PRIMARY_BREAKPOINTS } from './constants';

const LobbyPrimaryBlock = ({ blockTitleKey, blocks, imageUrl }) => {
  const [isShowImg, setIsShowImg] = useState(true);
  const handleError = () => setIsShowImg(false);

  const history = useHistory();
  const { authenticated } = useApplicationState();
  const { redirectToSignIn } = useSignInRedirects();

  const finalItems = useMemo(() => {
    if (!blocks || !Array.isArray(blocks)) return [];
    return blocks.map(item => ({
      ...item,
      iconUrl: item.imageUrl ? `${encodeURI(imageUrlCDN(item.imageUrl, CDN_TYPE_CMS))}` : '',
      id: item.id,
      key: crypto.randomUUID(),
      title: item.title,
    }));
  }, [blocks]);

  const handleAction = item => {
    const { actionType, gameId, url } = item;

    switch (actionType) {
      case LOBBY_PRIMARY_ACTION_TYPES.URL:
      case LOBBY_PRIMARY_ACTION_TYPES.CUSTOM_URL: {
        if (!url) return;
        const isExternal = url.startsWith('http://') || url.startsWith('https://');
        if (isExternal) {
          window.open(url, '_blank', 'noopener,noreferrer');
        } else {
          history.push(url);
        }
        break;
      }
      case LOBBY_PRIMARY_ACTION_TYPES.REFER_A_FRIEND:
        break;
      case LOBBY_PRIMARY_ACTION_TYPES.SMARTICO_MINI_GAMES:
      case LOBBY_PRIMARY_ACTION_TYPES.SMARTICO_MISSIONS:
      case LOBBY_PRIMARY_ACTION_TYPES.SMARTICO_STORE:
        break;
      case LOBBY_PRIMARY_ACTION_TYPES.TOURNAMENT: {
        const tournamentsPath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.TOURNAMENTS);
        history.push(tournamentsPath);
        break;
      }
      case LOBBY_PRIMARY_ACTION_TYPES.PROMOTION: {
        const promotionsPath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.PROMOTIONS);
        history.push(promotionsPath);
        break;
      }
      case LOBBY_PRIMARY_ACTION_TYPES.ACTIVE_GAME:
        if (gameId) {
          const casinoPath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.CASINO);
          const { game: casinoGamePath } = casinoRoutes;
          history.push(`${casinoPath}/${casinoGamePath}/${gameId}`);
        }
        break;
      default:
        break;
    }
  };

  const handleItemClick = item => {
    const { actionType } = item;

    if (!authenticated) {
      const requiresAuth = [
        LOBBY_PRIMARY_ACTION_TYPES.TOURNAMENT,
        LOBBY_PRIMARY_ACTION_TYPES.SMARTICO_STORE,
        LOBBY_PRIMARY_ACTION_TYPES.SMARTICO_MINI_GAMES,
        LOBBY_PRIMARY_ACTION_TYPES.SMARTICO_MISSIONS,
      ].includes(actionType);

      if (requiresAuth) {
        if (actionType === LOBBY_PRIMARY_ACTION_TYPES.TOURNAMENT) {
          const tournamentsPath = SiteConfigManager.getComponentPath(COMPONENT_TYPES.TOURNAMENTS);
          redirectToSignIn(null, { pathname: tournamentsPath });
        } else {
          redirectToSignIn();
        }
        return;
      }
    }

    handleAction(item);
  };

  const isRtl = useIsRtl();
  const dir = useMemo(() => (isRtl ? TEXT_DIRECTION.RTL : TEXT_DIRECTION.LTR), [isRtl]);

  if (!finalItems || finalItems.length === 0) {
    return null;
  }

  return (
    <div className="casino-lobby__item lobby-primary">
      <div className="casino-lobby__header">
        <div className="casino-lobby__left">
          {isShowImg && (
            <div className="casino-lobby__icon">
              <img
                alt={blockTitleKey}
                height={24}
                onError={handleError}
                role="presentation"
                src={
                  imageUrl
                    ? `${encodeURI(imageUrlCDN(imageUrl, CDN_TYPE_CMS))}`
                    : `${BASE_PATH || ''}/static/assets/images/primary.png`
                }
                width={24}
              />
            </div>
          )}
          {blockTitleKey && (
            <div className="casino-lobby__title">
              <Label message={blockTitleKey} />
            </div>
          )}
        </div>
      </div>
      <div className="casino-lobby__content">
        <div className="lobby-primary__slider">
          <Slider
            breakpoints={LOBBY_PRIMARY_BREAKPOINTS}
            breakpointsBase="container"
            dir={dir}
            direction="horizontal"
            loop={false}
            slidesPerView={15}
            spaceBetween={4}
          >
            {finalItems.map((item, index) => (
              <Slider.Slide key={item.key}>
                <CasinoPrimaryTile
                  index={index}
                  isLoading={false}
                  isMobile={true}
                  item={item}
                  onClick={() => handleItemClick(item)}
                />
              </Slider.Slide>
            ))}
          </Slider>
        </div>
      </div>
    </div>
  );
};

LobbyPrimaryBlock.propTypes = {
  blockTitleKey: PropTypes.string,
  blocks: PropTypes.array,
  imageUrl: PropTypes.string,
};

export default LobbyPrimaryBlock;
